import { apiClient } from "@/lib/api/client";
import { buildQueryString } from "@/lib/utils/query-string";

// Products
const PRODUCT_BASE = "/api/catalog/items/";

export async function listProducts(params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${PRODUCT_BASE}?${query}`);
  return data;
}

export async function getProduct(id: number) {
  const { data } = await apiClient.get(`${PRODUCT_BASE}${id}/`);
  return data;
}

export async function createProduct(payload: Record<string, unknown>) {
  const { data } = await apiClient.post(PRODUCT_BASE, payload);
  return data;
}

export async function updateProduct(id: number, payload: Record<string, unknown>) {
  const { data } = await apiClient.patch(`${PRODUCT_BASE}${id}/`, payload);
  return data;
}

export async function deleteProduct(id: number) {
  const { data } = await apiClient.delete(`${PRODUCT_BASE}${id}/`);
  return data;
}

export async function bulkUpdateProducts(ids: number[], values: Record<string, unknown>) {
  const { data } = await apiClient.patch(`${PRODUCT_BASE}bulk_update/`, { ids, data: { values } });
  return data;
}

export async function importProducts(file: FormData) {
  const { data } = await apiClient.post(`${PRODUCT_BASE}import_csv/`, file);
  return data;
}

export async function exportProductsCsv(params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${PRODUCT_BASE}export_csv/?${query}`, { responseType: "blob" });
  return data as Blob;
}

export async function exportProductsJson(params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${PRODUCT_BASE}export_json/?${query}`, { responseType: "blob" });
  return data as Blob;
}

export async function getProductItemGroup(itemId: number) {
  const { data } = await apiClient.get(`${PRODUCT_BASE}${itemId}/item_group/`);
  return data;
}

// Product Groups
const GROUP_BASE = "/api/catalog/group/";

export async function listProductGroups(params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${GROUP_BASE}?${query}`);
  return data;
}

export async function getProductGroup(id: number) {
  const { data } = await apiClient.get(`${GROUP_BASE}${id}/`);
  return data;
}

export async function createProductGroup(payload: FormData) {
  const { data } = await apiClient.post(GROUP_BASE, payload);
  return data;
}

export async function updateProductGroup(id: number, payload: Record<string, unknown>) {
  const { data } = await apiClient.patch(`${GROUP_BASE}${id}/`, payload);
  return data;
}

export async function deleteProductGroup(id: number) {
  const { data } = await apiClient.delete(`${GROUP_BASE}${id}/`);
  return data;
}

export async function uploadProductGroupImages(id: number, images: FormData) {
  const { data } = await apiClient.post(`${GROUP_BASE}${id}/`, images);
  return data;
}

export async function deleteProductGroupImage(id: number) {
  const { data } = await apiClient.delete(`${GROUP_BASE}image/${id}/`);
  return data;
}

export async function moveProductGroupImage(id: number, payload: Record<string, unknown>) {
  const { data } = await apiClient.post(`${GROUP_BASE}${id}/move_image/`, payload);
  return data;
}

// Attachments
const ATTACHMENT_BASE = "/api/catalog/attachments/";

export async function listAttachments(params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${ATTACHMENT_BASE}?${query}`);
  return data;
}

export async function getAttachment(id: number) {
  const { data } = await apiClient.get(`${ATTACHMENT_BASE}${id}/`);
  return data;
}

export async function createAttachment(payload: Record<string, unknown>) {
  const { data } = await apiClient.post(ATTACHMENT_BASE, payload);
  return data;
}

export async function updateAttachment(id: number, payload: Record<string, unknown>) {
  const { data } = await apiClient.patch(`${ATTACHMENT_BASE}${id}/`, payload);
  return data;
}

export async function deleteAttachment(id: number) {
  const { data } = await apiClient.delete(`${ATTACHMENT_BASE}${id}/`);
  return data;
}

export async function importAttachments(file: FormData) {
  const { data } = await apiClient.post(`${ATTACHMENT_BASE}import_csv/`, file);
  return data;
}

export async function exportAttachmentsCsv(params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${ATTACHMENT_BASE}export_csv/?${query}`, { responseType: "blob" });
  return data as Blob;
}

export async function exportAttachmentsJson(params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${ATTACHMENT_BASE}export_json/?${query}`, { responseType: "blob" });
  return data as Blob;
}

// Categories (staff)
const CATEGORY_BASE = "/api/catalog/category/";

export async function listCategories(params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${CATEGORY_BASE}?${query}`);
  return data;
}

export async function getCategoryDetail(id: number) {
  const { data } = await apiClient.get(`${CATEGORY_BASE}${id}/`);
  return data;
}

// Tags
export async function listTags() {
  const { data } = await apiClient.get("/api/core/tags/");
  return data;
}

// 2020 Catalog
export async function get2020Catalog() {
  const { data } = await apiClient.get("/api/catalog/2020/");
  return data;
}

export async function update2020Catalog(file: FormData) {
  const { data } = await apiClient.post("/api/catalog/2020/", file);
  return data;
}
